import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Card, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import {
  X,
  AlertCircle,
  FileText,
  Calendar,
  Hash,
  DollarSign,
  Tag,
  FilePlus
} from 'lucide-react';

/**
 * Skatteverket Transaction Details Modal
 * Shows transaction details and allows verification creation
 */
const SkatteverketTransactionDetailsModal = ({ show, onHide, transaction, onCreateVerification }) => {
  const { companyId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (show && transaction) {
      fetchSuggestions();
    }
  }, [show, transaction]);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = getApiUrl(
        `${companyId}/accounting/reconciliation/skatteverket/statements/${transaction.statement_id}/verification-suggestions`
      );

      const response = await axios.get(apiUrl, { withCredentials: true });

      if (response.data.success) {
        setSuggestions(response.data.suggestions || []);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError('Kunde inte hämta kontoförslag');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeLabel = (type) => {
    const labels = {
      'vat_payment': 'Momsbetalning',
      'vat_refund': 'Momsåterbetalning',
      'income_tax_payment': 'Inkomstskatt',
      'income_tax_refund': 'Återbetalning av inkomstskatt',
      'employer_tax_payment': 'Arbetsgivaravgifter',
      'employer_tax_refund': 'Återbetalning av arbetsgivaravgifter',
      'other': 'Övrigt'
    };
    return labels[type] || type;
  };

  const getTransactionTypeBadgeColor = (type) => {
    if (type.includes('vat')) return 'primary';
    if (type.includes('income_tax')) return 'info';
    if (type.includes('employer_tax')) return 'warning';
    return 'secondary';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('sv-SE');
  };

  if (!transaction) return null;

  const isPayment = parseFloat(transaction.amount) < 0;
  const isRefund = parseFloat(transaction.amount) > 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: isPayment ? '#fef2f2' : '#f0fdf4',
              color: isPayment ? '#dc2626' : '#16a34a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileText size={20} />
          </div>
          <div>
            <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937' }}>
              Skatteverket Transaktion
            </span>
            <div style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280' }}>
              {getTransactionTypeLabel(transaction.transaction_type)}
            </div>
          </div>
          <div className="ms-auto">
            <Badge bg={getTransactionTypeBadgeColor(transaction.transaction_type)}>
              {getTransactionTypeLabel(transaction.transaction_type)}
            </Badge>
          </div>
        </Modal.Title>
        <button
          type="button"
          className="btn-close"
          onClick={onHide}
          aria-label="Close"
        />
      </Modal.Header>

      <Modal.Body className="px-4 py-4">
        {/* Transaction Info Card */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <Calendar size={16} className="me-2" style={{ color: '#6b7280' }} />
                    <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Transaktionsdatum</strong>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                    {formatDate(transaction.transaction_date)}
                  </div>
                </div>

                {transaction.booking_date && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <Calendar size={16} className="me-2" style={{ color: '#6b7280' }} />
                      <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Bokföringsdatum</strong>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                      {formatDate(transaction.booking_date)}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <DollarSign size={16} className="me-2" style={{ color: '#6b7280' }} />
                    <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Belopp</strong>
                  </div>
                  <div
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: isRefund ? '#16a34a' : '#dc2626',
                      paddingLeft: '24px'
                    }}
                  >
                    {formatCurrency(transaction.amount)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', paddingLeft: '24px' }}>
                    {isPayment ? 'Betalning till Skatteverket' : 'Återbetalning från Skatteverket'}
                  </div>
                </div>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-1">
                    <FileText size={16} className="me-2" style={{ color: '#6b7280' }} />
                    <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Beskrivning</strong>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                    {transaction.description || '-'}
                  </div>
                </div>

                {transaction.period_reference && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <Tag size={16} className="me-2" style={{ color: '#6b7280' }} />
                      <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Periodreferens</strong>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                      {transaction.period_reference}
                    </div>
                  </div>
                )}

                {transaction.ocr_reference && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <Hash size={16} className="me-2" style={{ color: '#6b7280' }} />
                      <strong style={{ fontSize: '0.875rem', color: '#374151' }}>OCR-referens</strong>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                      {transaction.ocr_reference}
                    </div>
                  </div>
                )}

                {transaction.balance && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <DollarSign size={16} className="me-2" style={{ color: '#6b7280' }} />
                      <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Saldo</strong>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#1f2937', paddingLeft: '24px' }}>
                      {formatCurrency(transaction.balance)}
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <AlertCircle size={16} className="me-2" />
            {error}
          </Alert>
        )}

        {/* Account Suggestions */}
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Laddar kontoförslag...</span>
            </Spinner>
            <div className="mt-2" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Hämtar kontoförslag...
            </div>
          </div>
        )}

        {!loading && suggestions.length > 0 && (
          <Card>
            <Card.Header style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <strong style={{ fontSize: '1rem', color: '#1f2937' }}>Föreslagna Konton</strong>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                Baserat på transaktionstyp
              </div>
            </Card.Header>
            <Card.Body>
              {suggestions.map((suggestion, index) => (
                <Card
                  key={index}
                  className="mb-2"
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '1rem',
                    backgroundColor: suggestion.isDefault ? '#f0fdf4' : 'white'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="mb-2">
                        <Badge bg="success" style={{ fontSize: '0.875rem', marginRight: '8px' }}>
                          {(suggestion.confidence * 100).toFixed(0)}% säker
                        </Badge>
                        {suggestion.isDefault && (
                          <Badge bg="info" style={{ fontSize: '0.75rem' }}>
                            Rekommenderad
                          </Badge>
                        )}
                        {suggestion.isVATPeriodMatch && (
                          <Badge bg="primary" style={{ fontSize: '0.75rem', marginLeft: '8px' }}>
                            VAT-period matchning
                          </Badge>
                        )}
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1f2937' }}>
                        Debet: {suggestion.debitAccount} → Kredit: {suggestion.creditAccount}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                        {suggestion.reason}
                      </div>
                      {suggestion.description && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                          "{suggestion.description}"
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </Card.Body>
          </Card>
        )}

        {!loading && suggestions.length === 0 && (
          <Alert variant="info">
            <AlertCircle size={16} className="me-2" />
            Inga kontoförslag tillgängliga. Du kan skapa ett verifikat manuellt.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" onClick={onHide}>
          Stäng
        </Button>
        <Button
          variant="primary"
          onClick={() => onCreateVerification && onCreateVerification(transaction)}
        >
          <FilePlus size={16} className="me-2" />
          Skapa Verifikat
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SkatteverketTransactionDetailsModal;
